/**
 * Research Panel API Client
 *
 * NOTE ON NAMING: This module uses "learn" naming internally, but the feature
 * is displayed as "Research" in the UI. The "Learn" naming is historical.
 * All learn* endpoints support the "Research Panel" feature.
 */

import { api } from './client';

export const learnApi = {
  // ==========================================================================
  // CONVERSATIONS
  // ==========================================================================

  /**
   * Get or create a Learn conversation for a project.
   * @param {string} projectId - Project ID
   * @param {string|null} spawnContext - Optional context from gap analysis
   * @returns {Promise} Response with conversation_id, messages, spawn_context
   */
  getOrCreateConversation: (projectId, spawnContext = null) =>
    api.post(`/projects/${projectId}/learn/conversations`,
      spawnContext ? { spawn_context: spawnContext } : {}
    ),

  /**
   * Get messages for a Learn conversation.
   * @param {string} conversationId - Conversation ID
   * @returns {Promise} Response with messages array
   */
  getMessages: (conversationId) =>
    api.get(`/learn/conversations/${conversationId}/messages`),

  /**
   * Send a message to a Learn conversation and get AI response.
   * @param {string} conversationId - Conversation ID
   * @param {string} content - Message content
   * @returns {Promise} Response with user_message and assistant_message
   */
  sendMessage: (conversationId, content) =>
    api.post(`/learn/conversations/${conversationId}/messages`, { content }),

  // ==========================================================================
  // LIBRARY
  // ==========================================================================

  /**
   * Get the user's complete Learn Library.
   * @returns {Promise} Response with folders and uncategorized bookmarks
   */
  getLibrary: () =>
    api.get('/learn/library'),

  // ==========================================================================
  // FOLDERS
  // ==========================================================================

  /**
   * Create a new folder in the Learn Library.
   * @param {string} name - Folder name
   * @returns {Promise} Response with created folder
   */
  createFolder: (name) =>
    api.post('/learn/folders', { name }),

  /**
   * Update a folder's name and/or sort_order.
   * @param {string} folderId - Folder ID
   * @param {object} updates - { name?: string, sort_order?: number }
   * @returns {Promise} Response with updated folder
   */
  updateFolder: (folderId, updates) =>
    api.patch(`/learn/folders/${folderId}`, updates),

  /**
   * Delete a folder. Bookmarks move to uncategorized.
   * @param {string} folderId - Folder ID
   * @returns {Promise} Response with success status
   */
  deleteFolder: (folderId) =>
    api.delete(`/learn/folders/${folderId}`),

  // ==========================================================================
  // BOOKMARKS
  // ==========================================================================

  /**
   * Create a bookmark from a Learn message.
   * @param {string} messageId - Message ID to bookmark
   * @param {string|null} folderId - Optional folder to place bookmark in
   * @returns {Promise} Response with created bookmark
   */
  createBookmark: (messageId, folderId = null) =>
    api.post('/learn/bookmarks', { message_id: messageId, folder_id: folderId }),

  /**
   * Update a bookmark's folder assignment.
   * @param {string} bookmarkId - Bookmark ID
   * @param {string|null} folderId - New folder ID (null for uncategorized)
   * @returns {Promise} Response with updated bookmark
   */
  updateBookmark: (bookmarkId, folderId) =>
    api.patch(`/learn/bookmarks/${bookmarkId}`, { folder_id: folderId }),

  /**
   * Delete a bookmark.
   * @param {string} bookmarkId - Bookmark ID
   * @returns {Promise} Response with success status
   */
  deleteBookmark: (bookmarkId) =>
    api.delete(`/learn/bookmarks/${bookmarkId}`)
};
